import React, { useEffect, useState } from 'react';
import { ordersAPI, productsAPI, categoriesAPI, shopsAPI } from '../services/api';
import { Order, Product, Category, OrderStatus, PaymentType, Shop, OrderItem } from '../types';
import { useAuth } from '../contexts/AuthContext';
import OrderItemsModal from '../components/OrderItemsModal';
import ProductAttrs from '../components/ProductAttrs';
import './Orders.css';

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [orderItems, setOrderItems] = useState<Array<{ productId: number; quantity: number }>>([]);
  const [notes, setNotes] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedShopId, setSelectedShopId] = useState('');
  const [paymentType, setPaymentType] = useState<PaymentType>('CASH');
  const [dueDate, setDueDate] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 0 });
  const [itemsModal, setItemsModal] = useState<{ orderId: number; items: OrderItem[] } | null>(null);
  const { isShopOwner } = useAuth();

  useEffect(() => {
    fetchOrders();
    fetchCategories();
    shopsAPI.getAll({ limit: 100 }).then((r) => setShops(r.data)).catch(() => {});
  }, [page]);

  // Load all products once when form opens
  useEffect(() => {
    if (showForm && allProducts.length === 0) {
      productsAPI.getAll({ limit: 1000 }).then((r) => setAllProducts(r.data)).catch(console.error);
    }
  }, [showForm]);

  const fetchOrders = async () => {
    try {
      const data = await ordersAPI.getAll({ page, limit: 10 });
      setOrders(data.data);
      setMeta(data.meta);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await categoriesAPI.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

const handleAddItem = (productId: number) => {
    const product = allProducts.find((p) => p.id === productId);
    const maxQty = Number(product?.stock?.quantity ?? 0);
    const existingItem = orderItems.find((item) => item.productId === productId);
    if (existingItem) {
      if (existingItem.quantity >= maxQty) return;
      setOrderItems(
        orderItems.map((item) =>
          item.productId === productId
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        ),
      );
    } else {
      if (maxQty <= 0) return;
      setOrderItems([...orderItems, { productId, quantity: 1 }]);
    }
  };

  const handleUpdateQuantity = (productId: number, quantity: number) => {
    const product = allProducts.find((p) => p.id === productId);
    const maxQty = Number(product?.stock?.quantity ?? 0);
    if (quantity <= 0) {
      setOrderItems(orderItems.filter((item) => item.productId !== productId));
    } else {
      const clamped = Math.min(quantity, maxQty);
      setOrderItems(
        orderItems.map((item) =>
          item.productId === productId ? { ...item, quantity: clamped } : item,
        ),
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (orderItems.length === 0) {
      alert('Добавьте хотя бы один товар в заказ');
      return;
    }

    try {
      await ordersAPI.create({
        items: orderItems,
        notes: notes || undefined,
        shopId: selectedShopId ? Number(selectedShopId) : undefined,
        paymentType: selectedShopId ? paymentType : undefined,
        dueDate: paymentType === 'CREDIT' ? dueDate || undefined : undefined,
      });
      setShowForm(false);
      setOrderItems([]);
      setNotes('');
      setProductSearch('');
      setCategoryFilter('');
      setSelectedShopId('');
      setPaymentType('CASH');
      setDueDate('');
      fetchOrders();
      alert('Заказ создан!');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при создании заказа');
    }
  };

  const handleDelete = async (order: Order) => {
    if (order.status !== 'PENDING') {
      alert('Удалить можно только заказы в статусе "Ожидает"');
      return;
    }

    if (!window.confirm(`Удалить заказ #${order.id}?\n\nДействие необратимо!`)) {
      return;
    }

    try {
      await ordersAPI.delete(order.id);
      alert('Заказ удалён.');
      fetchOrders();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при удалении');
    }
  };

  const STATUS_LABELS: Record<OrderStatus, string> = {
    PENDING: 'Ожидает',
    CONFIRMED: 'Подтверждён',
    SHIPPED: 'Отправлен',
    DELIVERED: 'Доставлен',
    CANCELLED: 'Отменён',
    REJECTED: 'Отклонён',
  };

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'PENDING': return '#f39c12';
      case 'CONFIRMED': return '#3498db';
      case 'SHIPPED': return '#9b59b6';
      case 'DELIVERED': return '#27ae60';
      case 'CANCELLED': return '#e74c3c';
      case 'REJECTED': return '#dc2626';
      default: return '#95a5a6';
    }
  };

  if (loading) {
    return <div>Загрузка...</div>;
  }

  return (
    <>
    <div className="orders">
      <div className="page-header">
        <h1>Мои заказы</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          {showForm ? 'Отмена' : '+ Новый заказ'}
        </button>
      </div>

      {showForm && (
        <div className="form-card">
          <h2>Создать заказ</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Магазин {isShopOwner() && <span style={{ color: '#e53e3e' }}>*</span>}</label>
              <select
                value={selectedShopId}
                onChange={(e) => setSelectedShopId(e.target.value)}
                required={isShopOwner()}
              >
                <option value="">— {isShopOwner() ? 'Выберите магазин' : 'Без магазина'} —</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>{shop.name}</option>
                ))}
              </select>
            </div>
            {selectedShopId && (() => {
              const shop = shops.find((s) => String(s.id) === selectedShopId);
              if (!shop) return null;
              const debt = Number(shop.debt ?? 0);
              const advance = Number(shop.advance ?? 0);
              const limit = Number(shop.creditLimit ?? 0);
              const overLimit = limit > 0 && debt >= limit;
              const availableCredit = limit > 0 ? limit - debt : null;
              const maxDays = Number(shop.maxDebtDays);
              const maxDate = maxDays > 0
                ? (() => { const d = new Date(); d.setDate(d.getDate() + maxDays); return d.toISOString().split('T')[0]; })()
                : undefined;
              const fmtAmt = (n: number) => n.toLocaleString('ru-RU', { minimumFractionDigits: 2 });
              const boxClass = overLimit ? 'balance-over' : debt > 0 ? 'balance-debt' : advance > 0 ? 'balance-prepaid' : 'balance-ok';
              return (
                <>
                {/* Shop balance info */}
                <div className={`shop-balance-info ${boxClass}`}>
                  <div className="balance-info-row">
                    <span>💳 Долг:</span>
                    <strong style={{ color: debt > 0 ? (overLimit ? '#dc2626' : '#1e40af') : '#6b7280' }}>
                      {fmtAmt(debt)} сом
                    </strong>
                  </div>
                  <div className="balance-info-row">
                    <span>💚 Аванс:</span>
                    <strong style={{ color: advance > 0 ? '#065f46' : '#6b7280' }}>
                      {fmtAmt(advance)} сом
                    </strong>
                  </div>
                  {limit > 0 && (
                    <div className="balance-info-row">
                      <span>📊 Лимит:</span>
                      <span>{fmtAmt(limit)} сом</span>
                    </div>
                  )}
                  {availableCredit !== null && (
                    <div className="balance-info-row">
                      <span>📈 Доступно в долг:</span>
                      <strong style={{ color: overLimit ? '#dc2626' : '#065f46' }}>
                        {overLimit ? '⚠️ Лимит исчерпан!' : `${fmtAmt(availableCredit)} сом`}
                      </strong>
                    </div>
                  )}
                  {limit === 0 && (
                    <div className="balance-info-row">
                      <span>📊 Лимит:</span>
                      <span style={{ color: '#6b7280' }}>без ограничений</span>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Тип оплаты</label>
                  <div className="payment-type-toggle">
                    <label className={`toggle-opt ${paymentType === 'CASH' ? 'toggle-active' : ''}`}>
                      <input type="radio" name="paymentType" value="CASH"
                        checked={paymentType === 'CASH'}
                        onChange={() => { setPaymentType('CASH'); setDueDate(''); }} />
                      💵 Оплата на месте
                    </label>
                    <label className={`toggle-opt ${paymentType === 'CREDIT' ? 'toggle-active' : ''}`}>
                      <input type="radio" name="paymentType" value="CREDIT"
                        checked={paymentType === 'CREDIT'}
                        onChange={() => {
                          setPaymentType('CREDIT');
                          if (maxDate) setDueDate(maxDate);
                          else setDueDate(new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0]);
                        }} />
                      📋 В долг
                    </label>
                    {advance > 0 && (
                      <label className={`toggle-opt ${paymentType === 'ADVANCE' ? 'toggle-active toggle-advance' : ''}`}>
                        <input type="radio" name="paymentType" value="ADVANCE"
                          checked={paymentType === 'ADVANCE'}
                          onChange={() => { setPaymentType('ADVANCE'); setDueDate(''); }} />
                        💚 С аванса ({advance.toLocaleString('ru-RU')} с)
                      </label>
                    )}
                  </div>
                  {paymentType === 'CREDIT' && (
                    <div style={{ marginTop: 8 }}>
                      <label style={{ fontSize: 13, color: '#374151' }}>
                        Дата погашения {maxDays > 0 && <span style={{ color: '#6b7280' }}>(макс. {maxDays} дн.)</span>}
                      </label>
                      <input type="date" value={dueDate}
                        min={new Date().toISOString().split('T')[0]}
                        max={maxDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
                    </div>
                  )}
                </div>
                </>
              );
            })()}
            {/* ── Product picker ── */}
            <div className="form-group">
              <label>Товары</label>

              {/* Search */}
              <input
                type="text"
                className="product-search"
                placeholder="🔍 Поиск по названию..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />

              {/* Category filter */}
              <select
                className="product-search"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{ marginBottom: 10 }}
              >
                <option value="">Все категории</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>

              {/* Product table */}
              <div className="picker-table-wrap">
                <table className="picker-table">
                  <thead>
                    <tr>
                      <th>Название</th>
                      <th>Цена</th>
                      <th>Склад</th>
                      <th style={{ width: 160 }}>Количество</th>
                      <th style={{ width: 100, textAlign: 'right' }}>Сумма</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allProducts
                      .filter((p) => {
                        const matchSearch = !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase());
                        const matchCat = !categoryFilter || String(p.categoryId) === categoryFilter;
                        return matchSearch && matchCat;
                      })
                      .map((product) => {
                        const item = orderItems.find((i) => i.productId === product.id);
                        const unit = product.unit === 'PIECE' ? 'шт.' : product.unit === 'METER' ? 'м.' : 'кг.';
                        const stock = Number(product.stock?.quantity ?? 0);
                        const subtotal = item ? Number(product.price) * item.quantity : 0;
                        const inOrder = !!item;
                        return (
                          <tr key={product.id} className={inOrder ? 'picker-row-selected' : ''}>
                            <td>
                              <span className="picker-name">{product.name}</span>
                              <span className="picker-cat">{product.category?.name}</span>
                              <ProductAttrs attributes={product.attributes} />
                            </td>
                            <td className="picker-price">{Number(product.price).toLocaleString('ru-RU')} с</td>
                            <td className={stock === 0 ? 'picker-stock-zero' : stock < 10 ? 'picker-stock-low' : 'picker-stock-ok'}>
                              {stock} {unit}
                            </td>
                            <td>
                              {inOrder ? (
                                <div className="quantity-controls">
                                  <button type="button" onClick={() => handleUpdateQuantity(product.id, item.quantity - 1)} className="btn-quantity">−</button>
                                  <input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => {
                                      const val = product.unit === 'PIECE' ? parseInt(e.target.value) || 0 : parseFloat(e.target.value) || 0;
                                      handleUpdateQuantity(product.id, val);
                                    }}
                                    min={product.unit === 'PIECE' ? '1' : '0.01'}
                                    max={stock}
                                    step={product.unit === 'PIECE' ? '1' : '0.01'}
                                    className="quantity-input"
                                  />
                                  <button type="button" onClick={() => handleUpdateQuantity(product.id, item.quantity + 1)} className="btn-quantity" disabled={item.quantity >= stock}>+</button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleAddItem(product.id)}
                                  className="btn-add-row"
                                  disabled={stock === 0}
                                >
                                  {stock === 0 ? 'Нет' : '+ Добавить'}
                                </button>
                              )}
                            </td>
                            <td className="picker-subtotal">
                              {inOrder ? `${subtotal.toLocaleString('ru-RU')} с` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {orderItems.length > 0 && (
              <div className="form-group">
                <label>Состав заказа</label>
                <div className="order-summary">
                  <div className="summary-header">
                    <span>Товар</span>
                    <span>Кол-во</span>
                    <span>Цена</span>
                    <span>Сумма</span>
                  </div>
                  {orderItems.map((item) => {
                    const product = allProducts.find((p) => p.id === item.productId);
                    const unit = product?.unit === 'PIECE' ? 'шт.' : product?.unit === 'METER' ? 'м.' : 'кг.';
                    const price = Number(product?.price ?? 0);
                    const subtotal = price * item.quantity;
                    return (
                      <div key={item.productId} className="summary-item">
                        <span className="summary-name">{product?.name || `Товар #${item.productId}`}</span>
                        <span className="summary-qty">{item.quantity} {unit}</span>
                        <span className="summary-price">{price.toLocaleString('ru-RU')} с</span>
                        <span className="summary-sub">{subtotal.toLocaleString('ru-RU')} с</span>
                      </div>
                    );
                  })}
                  <div className="summary-total">
                    <span>Итого:</span>
                    <span>{orderItems.reduce((sum, item) => {
                      const product = allProducts.find((p) => p.id === item.productId);
                      return sum + Number(product?.price ?? 0) * item.quantity;
                    }, 0).toLocaleString('ru-RU')} с</span>
                  </div>
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Примечание (необязательно)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Дополнительная информация к заказу..."
              />
            </div>

            <button type="submit" className="btn-primary" disabled={orderItems.length === 0}>
              Создать заказ
            </button>
          </form>
        </div>
      )}

      <div className="table-container orders-desktop-table">
        <div className="pagination-controls">
          <button onClick={() => setPage(page - 1)} disabled={page === 1} className="btn-pagination">← Назад</button>
          <span>Страница {page} из {meta.totalPages || 1}</span>
          <button onClick={() => setPage(page + 1)} disabled={page >= (meta.totalPages || 1)} className="btn-pagination">Вперёд →</button>
        </div>

        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Магазин</th>
              <th>Статус</th>
              <th>Оплата</th>
              <th>Сумма</th>
              <th>Срок долга</th>
              <th>Товары</th>
              <th>Дата</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id}>
                <td>#{order.id}</td>
                <td>
                  {order.shop ? (
                    <div>
                      <div>{order.shop.name}</div>
                      <small style={{ fontWeight: 600, color:
                        Number(order.shop.creditLimit) > 0 && Number(order.shop.debt) >= Number(order.shop.creditLimit) ? '#dc2626' :
                        Number(order.shop.debt) > 0 ? '#2563eb' : '#9ca3af'
                      }}>
                        {Number(order.shop.debt) > 0 ? `Долг: ${Number(order.shop.debt).toLocaleString('ru-RU')} с` : 'Без долга'}
                        {Number(order.shop.advance) > 0 && ` / Аванс: +${Number(order.shop.advance).toLocaleString('ru-RU')} с`}
                      </small>
                    </div>
                  ) : <span style={{ color: '#9ca3af' }}>—</span>}
                </td>
                <td>
                  <span className="status-badge" style={{ backgroundColor: getStatusColor(order.status) }}>
                    {STATUS_LABELS[order.status]}
                  </span>
                  {order.rejectionReason && (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#dc2626', fontWeight: 500 }}>
                      ❌ {order.rejectionReason}
                    </div>
                  )}
                </td>
                <td>
                  {order.paymentType === 'CASH' && <span style={{ color: '#374151' }}>💵 Наличные</span>}
                  {order.paymentType === 'CREDIT' && <span style={{ color: '#1e40af', fontWeight: 600 }}>📋 В долг</span>}
                  {order.paymentType === 'ADVANCE' && <span style={{ color: '#065f46', fontWeight: 600 }}>💚 С аванса</span>}
                </td>
                <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {Number(order.totalAmount).toLocaleString('ru-RU')} сом
                </td>
                <td>{(() => {
                  if (!order.dueDate) return <span style={{ color: '#9ca3af' }}>—</span>;
                  const today = new Date().toISOString().split('T')[0];
                  const overdue = order.dueDate < today && order.status !== 'DELIVERED' && order.status !== 'CANCELLED';
                  return (
                    <span style={{ color: overdue ? '#ef4444' : '#374151', fontWeight: overdue ? 700 : 400 }}>
                      {overdue && '⚠️ '}
                      {new Date(order.dueDate).toLocaleDateString('ru-RU')}
                    </span>
                  );
                })()}</td>
                <td>
                  {order.items && order.items.length > 0 ? (
                    <button className="btn-items-link" onClick={() => setItemsModal({ orderId: order.id, items: order.items! })}>
                      {order.items.length} поз.
                    </button>
                  ) : '—'}
                </td>
                <td>{new Date(order.createdAt).toLocaleDateString('ru-RU')}</td>
                <td>
                  {order.status === 'PENDING' && (
                    <button onClick={() => handleDelete(order)} className="btn-delete">Удалить</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Orders Card List */}
      <div className="orders-mobile-list">
        <div className="pagination-controls">
          <button onClick={() => setPage(page - 1)} disabled={page === 1} className="btn-pagination">← Назад</button>
          <span>Страница {page} из {meta.totalPages || 1}</span>
          <button onClick={() => setPage(page + 1)} disabled={page >= (meta.totalPages || 1)} className="btn-pagination">Вперёд →</button>
        </div>

        {orders.length === 0 ? (
          <div className="mobile-empty-card">Заказов не найдено</div>
        ) : (
          orders.map((order) => {
            const isOverdue = (() => {
              if (!order.dueDate) return false;
              const today = new Date().toISOString().split('T')[0];
              return order.dueDate < today && order.status !== 'DELIVERED' && order.status !== 'CANCELLED';
            })();

            return (
              <div key={order.id} className="mobile-order-card">
                <div className="mobile-card-head">
                  <div className="mobile-card-head-left">
                    <span className="mobile-order-id">#{order.id}</span>
                    <span className="mobile-shop-name">{order.shop?.name || '—'}</span>
                  </div>
                  <span className="status-badge" style={{ backgroundColor: getStatusColor(order.status) }}>
                    {STATUS_LABELS[order.status]}
                  </span>
                </div>

                {order.rejectionReason && (
                  <div className="mobile-reject-reason">
                    ❌ {order.rejectionReason}
                  </div>
                )}

                <div className="mobile-card-details">
                  <div className="mobile-detail-row">
                    <span className="mobile-detail-label">Оплата:</span>
                    <span className="mobile-detail-val">
                      {order.paymentType === 'CASH' && '💵 Наличные'}
                      {order.paymentType === 'CREDIT' && '📋 В долг'}
                      {order.paymentType === 'ADVANCE' && '💚 С аванса'}
                    </span>
                  </div>

                  {order.dueDate && (
                    <div className="mobile-detail-row">
                      <span className="mobile-detail-label">Срок оплаты:</span>
                      <span className="mobile-detail-val" style={{ color: isOverdue ? '#ef4444' : '#334155', fontWeight: isOverdue ? 700 : 400 }}>
                        {isOverdue && '⚠️ '}{new Date(order.dueDate).toLocaleDateString('ru-RU')}
                      </span>
                    </div>
                  )}

                  <div className="mobile-detail-row">
                    <span className="mobile-detail-label">Товары:</span>
                    <span className="mobile-detail-val">
                      {order.items && order.items.length > 0 ? (
                        <button className="btn-items-link" onClick={() => setItemsModal({ orderId: order.id, items: order.items! })}>
                          🔍 {order.items.length} поз. (открыть)
                        </button>
                      ) : '—'}
                    </span>
                  </div>

                  <div className="mobile-detail-row">
                    <span className="mobile-detail-label">Дата:</span>
                    <span className="mobile-detail-val">{new Date(order.createdAt).toLocaleDateString('ru-RU')}</span>
                  </div>
                </div>

                <div className="mobile-card-foot">
                  <div className="mobile-amount-wrap">
                    <span className="mobile-amount-label">Сумма:</span>
                    <span className="mobile-amount-val">{Number(order.totalAmount).toLocaleString('ru-RU')} сом</span>
                  </div>
                  {order.status === 'PENDING' && (
                    <button onClick={() => handleDelete(order)} className="btn-delete">Удалить</button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>

    {itemsModal && (
      <OrderItemsModal
        orderId={itemsModal.orderId}
        items={itemsModal.items}
        onClose={() => setItemsModal(null)}
      />
    )}
    </>
  );
};

export default Orders;
